import {
  Box,
  ListItemButton,
  Tooltip,
  ListItemButtonProps,
  PopoverOrigin,
  Typography,
  Divider,
} from '@mui/material';
import React, { CSSProperties, useCallback, useMemo, useState } from 'react';
import SelectMenu from '..';
import { NodonMenuItemProps, SelectListChildren } from '../../menu.model';
import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import { closeMenu } from '@client/hooks/use-menu.hooks';

interface ItemProps<T> extends Omit<NodonMenuItemProps, 'items'> {
  items?: T[];
}

interface ListItemProps<T extends ItemProps<T>> extends Omit<
  ListItemButtonProps,
  'children'
> {
  children: SelectListChildren<T>;
  item: T;
  tooltip?: string;
  style?: CSSProperties;
}

const SelectMenuNestedItem = <T extends ItemProps<T>>({
  children,
  style,
  item,
  tooltip,
  ...buttonProps
}: ListItemProps<T>) => {
  const { items, subheader } = item;
  const [anchor, setAnchor] = useState<Element>();

  const origin = useMemo<PopoverOrigin>(() => {
    const horizontalOffset = anchor?.clientWidth ?? 100;
    return { vertical: 'top', horizontal: -horizontalOffset };
  }, [anchor]);

  const handleItemClick = useMouseEventCallback((e) => {
    if (items) {
      setAnchor(e.currentTarget);
      return;
    }

    buttonProps.onClick?.(e as React.MouseEvent<HTMLDivElement>);
    closeMenu();
  });

  const handleCloseSubMenu = useCallback(() => {
    setAnchor(undefined);
  }, []);

  const renderSubItems = useCallback<SelectListChildren<T>>(
    (subItem) => {
      const { id, tooltip, divider, disabled, onClick } = subItem;
      const props: Partial<ListItemProps<T>> = {
        tooltip,
        divider,
        disabled,
        onClick,
      };
      return (
        <SelectMenuNestedItem key={id} item={subItem} {...props}>
          {children}
        </SelectMenuNestedItem>
      );
    },
    [children],
  );

  return (
    <>
      <Tooltip style={style} title={tooltip} disableInteractive>
        {subheader ? (
          <div>
            {item.id !== '0' && <Divider sx={{ my: 1 }} />}
            {item.label && (
              <Box
                sx={{
                  p: 2,
                }}
              >
                <Typography variant="menuTitle">{item.label}</Typography>
              </Box>
            )}
          </div>
        ) : (
          <Box>
            <ListItemButton
              {...buttonProps}
              onClick={handleItemClick}
              divider={item.divider}
            >
              {children(item)}
            </ListItemButton>
          </Box>
        )}
      </Tooltip>

      {items && items.length > 0 && (
        <SelectMenu
          items={items}
          anchor={anchor}
          transformOrigin={origin}
          onClose={handleCloseSubMenu}
        >
          {renderSubItems}
        </SelectMenu>
      )}
    </>
  );
};

export default SelectMenuNestedItem;
